import React, { useContext, useState } from 'react'
import { GoogleLogin, GoogleLoginResponse, GoogleLoginProps } from 'react-google-login'
import { useAuthenticateByExternalOAuthTokenMutation } from '../../../OAuthLogin/authenticateByExternalOAuthToken.generated'
import { AuthenticatedUserFragment } from '../../../../fragments/AuthenticatedUser.generated'
import { OAuthType } from '../../../../generated/types'
import { useRegisterCustomerMutation } from '../../../OAuthRegister/RegisterCustomer.generated'
import GoogleIconMark from './google-iconmark.svg'
import config from '#src/config'
import classnames from 'classnames'
import styles from './styles.css'
import { isConsumerMobileNative, isBusinessMobileNative } from '#src/services/mobile'
import { GoogleSigninResponse } from '#src/services/dealNativeBridge'
import { WebMessageEventEmitterContext } from '#src/containers/ReceiveMessageFromNative'
import uuidv4 from 'uuid/v4'

interface GoogleOAuthButtonProps
  extends Omit<GoogleLoginProps, 'clientId' | 'responseType' | 'onSuccess' | 'onFailure'> {
  wrapperClassName?: string
  referralCodeId?: string | null
  registrationId?: string | null
  onSuccess: (user: AuthenticatedUserFragment) => void
  onFailure: (error: string) => void
}

const GoogleOAuthButton: React.FC<GoogleOAuthButtonProps> = ({
  className,
  wrapperClassName,
  referralCodeId,
  registrationId,
  onSuccess,
  onFailure,
  ...rest
}) => {
  const [registerCustomer] = useRegisterCustomerMutation()
  const [authenticateByExternalOAuthToken] = useAuthenticateByExternalOAuthTokenMutation()
  const eventEmitter = useContext(WebMessageEventEmitterContext)
  const [googleIsDisabled, setGoogleIsDisabled] = useState(false)

  /**
   * After successfully completing the Google OAuth flow, submit the ID token to our API
   *   for authentication.
   */
  const handleWebSuccess = (response: GoogleLoginResponse) => {
    handleSuccess({
      firstName: response.getBasicProfile().getGivenName(),
      lastName: response.getBasicProfile().getFamilyName(),
      email: response.getBasicProfile().getEmail(),
      idToken: response.getAuthResponse().id_token
    })
  }

  const handleSuccess = ({
    firstName,
    lastName,
    email,
    idToken
  }: {
    firstName: string | null
    lastName: string | null
    email: string | null
    idToken: string
  }) => {
    if (isBusinessMobileNative()) {
      // For expert native app, do not allow registering users.
      authenticateByExternalOAuthToken({
        variables: {
          input: {
            token: idToken,
            type: OAuthType.GOOGLE
          }
        }
      }).then(response => {
        const errors = response.errors

        if (errors) {
          onFailure('Google authentication failed!')
          return
        }

        const payload = response.data!.authenticateByExternalOAuthToken

        if (payload.user) {
          onSuccess(payload.user)
        } else {
          onFailure(payload.message || 'Unable to authenticate, maybe you do not have an account?')
        }
      })
    } else {
      registerCustomer({
        variables: {
          input: {
            firstName,
            lastName,
            email,
            externalOAuthType: OAuthType.GOOGLE,
            externalOAuthToken: idToken,
            referralCodeId,
            registrationId
          }
        }
      }).then(response => {
        const errors = response.errors

        // Handle failures at the GraphQL level
        if (errors) {
          onFailure('Google authentication failed!')
          return
        }

        const payload = response.data!.registerCustomer

        if (payload.user) {
          onSuccess(payload.user)
        } else {
          onFailure(
            (payload.errors && payload.errors.join('\n')) || 'Google authentication failed!'
          )
        }
      })
    }
  }

  /**
   * Handle failures from the Google OAuth flow itself.
   */
  const handleFailure = (failure: { error: string }) => {
    switch (failure.error) {
      // TODO: Should we surface different messages for these error codes?
      case 'idpiframe_initialization_failed':
        setGoogleIsDisabled(true)
        break
      case 'popup_closed_by_user':
      case 'access_denied':
      case 'immediate_failed':
      default:
        onFailure('Google authentication failed!')
    }
  }

  const handleNativeClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    eventEmitter!
      .requestResponse<GoogleSigninResponse>(
        {
          type: 'googleSigninRequest',
          messageId: uuidv4()
        },
        'googleSigninResponse'
      )
      .then(message => {
        if (message.success) {
          handleSuccess({
            idToken: message.success.idToken,
            firstName: message.success.user.givenName,
            lastName: message.success.user.familyName,
            email: message.success.user.email
          })
        } else {
          handleFailure({ error: message.failureCode!.toString() })
        }
      })
  }

  if ((isConsumerMobileNative() || isBusinessMobileNative()) && eventEmitter) {
    return (
      <div className={classnames(styles.wrapper, wrapperClassName)}>
        <div className={classnames(styles.button, className)} onClick={handleNativeClick}>
          <GoogleIconMark className={styles.logo} /> Continue with Google
        </div>
      </div>
    )
  } else {
    return (
      <div className={classnames(styles.wrapper, wrapperClassName)} {...rest}>
        <GoogleLogin
          className={classnames(styles.button, className)}
          clientId={config.get('google.client_id')}
          disabled={googleIsDisabled}
          responseType="id_token"
          onSuccess={response => handleWebSuccess(response as GoogleLoginResponse)}
          onFailure={handleFailure}
        >
          <GoogleIconMark className={styles.logo} /> Continue with Google
        </GoogleLogin>
      </div>
    )
  }
}

export default GoogleOAuthButton
